import { characterOptions } from '../../../content/characters/character-options.js';
import { SKILLS, STATS, STAT_LABELS } from '../../../shared/game/character-build.js';
import type { GameContent, HeroDefinition, HeroState } from '../../../shared/game/world-types.js';
import { icon } from '../icons.js';
import { signed } from './inventory-model.js';

export const node = <K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = '') => {
  const result = document.createElement(tag); result.className = className; result.textContent = text; return result;
};
const heading = (title: string) => node('h3', 'inventory-section-title', title);
const rows = (values: [string, string][]) => {
  const list = node('dl', 'inventory-facts');
  for (const [label, value] of values) list.append(node('dt', '', label), node('dd', '', value));
  return list;
};

export function renderStats(host: HTMLElement, definition: HeroDefinition, hero: HeroState) {
  const attributes = node('div', 'inventory-attributes');
  for (const stat of STATS) {
    const tile = node('div', 'inventory-attribute');
    tile.append(icon(stat === 'strength' ? 'strength' : stat === 'dexterity' ? 'movement' : stat === 'intelligence' ? 'intellect' : 'willpower'), node('span', '', STAT_LABELS[stat]), node('strong', '', signed(definition.profile.attributes[stat])));
    const saves = definition.profile.saves;
    tile.append(node('small', '', saves?.advantage.includes(stat) ? 'Save advantage' : saves?.disadvantage.includes(stat) ? 'Save disadvantage' : 'Normal save'));
    attributes.append(tile);
  }
  const health = hero.health;
  host.replaceChildren(heading('Attributes & saves'), attributes, heading('Combat & condition'), rows([
    ['Hit points', `${health.hp} / ${health.maxHp}`], ['Temporary HP', String(health.temporaryHp)], ['Wounds', `${health.wounds} / ${health.maxWounds}`],
    ['Hit Dice', `${health.hitDice ?? 0} / ${health.maxHitDice ?? 0}`], ['Defense', String(definition.profile.defense)], ['Defend cost', `${definition.profile.defendCost ?? 1} action${(definition.profile.defendCost ?? 1) === 1 ? '' : 's'}`],
    ['Initiative', `${signed(definition.profile.initiative ?? 0)}${definition.profile.initiativeAdvantage ? ' · Advantage' : ''}`], ['Speed', `${definition.profile.movementDistance / 1.5} spaces`], ['Condition', health.dead ? 'Dead' : health.dying ? 'Dying' : hero.prone ? 'Prone' : health.wounds ? 'Wounded' : 'Healthy'],
  ]));
  const ancestry = characterOptions.ancestries.find(candidate => candidate.id === definition.ancestryId);
  if (ancestry) {
    host.append(heading(`${ancestry.name} · ${ancestry.trait}`), node('p', 'inventory-copy', ancestry.traitDescription ?? ancestry.description));
    if (definition.relentless) host.append(node('p', 'inventory-note', health.relentlessAvailable ? 'Relentless available · recovers on a Safe Rest' : 'Relentless spent · recovers on a Safe Rest'));
    if (definition.elusive) host.append(node('p', 'inventory-note', health.elusiveAvailable ? 'Elusive available · recovers on a Safe Rest' : 'Elusive spent · recovers on a Safe Rest'));
  }
  if (definition.languages?.length) host.append(rows([['Languages', definition.languages.map(language => language.name).join(', ')]]));
}

export function renderSkills(host: HTMLElement, definition: HeroDefinition) {
  const list = node('div', 'inventory-skills');
  for (const skill of SKILLS) {
    const row = node('div', 'inventory-skill');
    row.append(node('span', '', skill.name), node('small', '', STAT_LABELS[skill.stat]), node('strong', '', signed(definition.skills?.[skill.id] ?? definition.profile.attributes[skill.stat])));
    list.append(row);
  }
  host.replaceChildren(heading('Skills'), list);
}

export function renderPowers(host: HTMLElement, definition: HeroDefinition, hero: HeroState, content: GameContent) {
  const cls = content.classes?.find(candidate => candidate.id === definition.classId);
  host.replaceChildren(heading(`${cls?.name ?? definition.classId} · Level ${hero.class.level}`));
  if (!cls) { host.append(node('p', 'inventory-copy', 'No class powers listed.')); return; }
  for (const ability of cls.abilities.filter(ability => ability.level <= hero.class.level)) {
    const card = node('article', 'inventory-power');
    const charges = hero.class.abilities.find(state => state.id === ability.id)?.charges ?? 0;
    card.append(node('h4', '', ability.name), node('p', 'inventory-copy', ability.description), node('p', 'inventory-note', `${ability.actions ? `${ability.actions} action${ability.actions === 1 ? '' : 's'}` : 'Free action'}${ability.usesPerEncounter ? ` · ${charges}/${ability.usesPerEncounter} uses this encounter` : ''}${ability.combatOnly ? ' · During combat' : ''}`));
    host.append(card);
  }
  const passives = cls.passives.filter(feature => feature.level <= hero.class.level);
  if (passives.length) host.append(heading('Passive features'));
  for (const passive of passives) { const card = node('article', 'inventory-power'); card.append(node('h4', '', passive.name), node('p', 'inventory-copy', passive.description)); host.append(card); }
}
